import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useRazorpay } from "react-razorpay";
import { CONFIG, waLink } from "./config";
import { trackEvent as track } from "./lib/analytics";
import { COLOR_HEX } from "./admin";
import { useCart, imgSrc } from "./store";
import { Footer, Header, WhatsAppButton } from "./components/layout";
import { Chapter, Reveal } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useProducts(params = {}) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/products`, { params })
      .then(({ data }) => active && setProducts(data))
      .catch(() => active && setProducts([]))
      .finally(() => active && setLoading(false));
    return () => (active = false);
  }, [JSON.stringify(params)]);
  return { products, loading };
}

export const SIZE_GUIDE = [
  ["S", '34"'], ["M", '36"'], ["L", '38"'], ["XL", '40"'], ["2XL", '42"'], ["3XL", '44"'], ["4XL", '46"'],
];

export function SizeGuideModal({ onClose }) {
  return (
    <div className="size-modal-backdrop" data-testid="size-guide-modal" onClick={onClose} role="dialog" aria-modal="true" aria-label="Size guide">
      <div className="size-modal" onClick={(e) => e.stopPropagation()}>
        <div className="size-modal-head">
          <h3>Size Guide</h3>
          <button data-testid="size-guide-close" className="icon-button" onClick={onClose} aria-label="Close size guide"><X size={16} /></button>
        </div>
        <table>
          <thead><tr><th>Size</th><th>Bust (tape)</th></tr></thead>
          <tbody>
            {SIZE_GUIDE.map(([s, inch]) => (
              <tr key={s}><td>{s}</td><td>{inch}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="size-note">Tape numbers (28″–50″) show the actual bust measurement in inches. Measure around the fullest part of the bust, keeping the tape level. If between sizes, pick the larger one — or ask us on WhatsApp.</p>
      </div>
    </div>
  );
}

export function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const [size, setSize] = useState(product.sizes?.[0] || "");
  const [color, setColor] = useState(product.colors?.[0] || "");
  const [showGuide, setShowGuide] = useState(false);
  const hasPrice = product.price != null;
  return (
    <Reveal delay={index * 0.05}>
      <div className="product-card" data-testid={`product-card-${product.id}`}>
        <div className="product-image">
          {product.images?.[0] ? (
            <img loading="lazy" src={imgSrc(product.images[0])} alt={product.name} />
          ) : (
            <div className="asset-slot"><span className="asset-slot-label">PHOTO SOON</span></div>
          )}
          {!product.in_stock && <span className="stock-badge">Sold out</span>}
          {product.new_arrival && <span className="new-badge">NEW</span>}
          {product.mrp != null && product.price != null && product.mrp > product.price && (
            <span className="off-badge">{Math.round(((product.mrp - product.price) / product.mrp) * 100)}% OFF</span>
          )}
        </div>
        <div className="product-meta">
          <h3>{product.name}</h3>
          {product.subcategory && <p>{product.subcategory}</p>}
          {hasPrice ? (
            <span className="price-row">
              {product.mrp != null && product.mrp > product.price && <s className="mrp">₹{product.mrp.toLocaleString("en-IN")}</s>}
              <strong className="price">₹{product.price.toLocaleString("en-IN")}</strong>
              {product.mrp != null && product.mrp > product.price && (
                <em className="off-tag">{Math.round(((product.mrp - product.price) / product.mrp) * 100)}% OFF</em>
              )}
            </span>
          ) : (
            <span className="price-ask">Price on enquiry</span>
          )}
          {product.sizes?.length > 0 && (
            <>
              <div className="size-row">
                {product.sizes.map((s) => (
                  <button key={s} data-testid={`size-${product.id}-${s}`} className={size === s ? "size-chip active" : "size-chip"} onClick={() => setSize(s)}>
                    {/^\d+$/.test(s) ? `${s}"` : s}
                  </button>
                ))}
              </div>
              <button data-testid={`size-guide-${product.id}`} className="size-guide-link" onClick={() => setShowGuide(true)}>Size guide</button>
            </>
          )}
          {product.colors?.length > 0 && (
            <div className="color-row" data-testid={`colors-${product.id}`}>
              {product.colors.map((c) => {
                const hex = COLOR_HEX[c];
                return (
                  <button
                    key={c}
                    data-testid={`color-${product.id}-${c.toLowerCase()}`}
                    className={color === c ? "color-dot-pick active" : "color-dot-pick"}
                    title={c}
                    aria-label={`Select color ${c}`}
                    onClick={() => setColor(c)}
                  >
                    <i className="color-dot" style={hex === "multi" || !hex ? { background: "linear-gradient(135deg,#C0392B,#C9A227,#1E8449,#2E5EAA,#7D3C98)" } : { background: hex }} />
                  </button>
                );
              })}
              <span className="color-name">{color}</span>
            </div>
          )}
          <div className="product-actions">
            {hasPrice && product.in_stock ? (
              <button data-testid={`add-to-cart-${product.id}`} className="button primary small" onClick={() => { add(product, size, color); track("add_to_cart", { product: product.name }); }}>
                <ShoppingBag size={14} /> Add to bag
              </button>
            ) : null}
            <WhatsAppButton label="Enquire" context={product.name} testId={`whatsapp-product-${product.id}`} className="wa-small" />
          </div>
        </div>
      </div>
      {showGuide && <SizeGuideModal onClose={() => setShowGuide(false)} />}
    </Reveal>
  );
}

export function ProductsGrid({ params, emptyText }) {
  const { products, loading } = useProducts(params);
  if (loading) return <p className="catalogue-note">Loading collection…</p>;
  if (!products.length) return <p className="catalogue-note" data-testid="no-products-note">{emptyText}</p>;
  return (
    <div className="product-grid">
      {products.map((p, i) => (
        <ProductCard key={p.id} product={p} index={i} />
      ))}
    </div>
  );
}

export function CartPage() {
  const { items, setQty, remove, total, clear } = useCart();
  const navigate = useNavigate();
  return (
    <>
      <Header />
      <main className="inner-page">
        <Chapter index="—" eyebrow="YOUR SELECTION" title={<>Shopping <em>bag</em></>} text={items.length ? "Review your picks and check out when ready." : "Your bag is empty — explore the collections to begin."} />
        {items.length > 0 && (
          <>
            <div className="cart-list">
              {items.map((i) => (
                <div className="cart-row" data-testid={`cart-item-${i.id}`} key={`${i.id}:${i.size}:${i.color || ""}`}>
                  {i.image && <img src={imgSrc(i.image)} alt={i.name} />}
                  <div className="cart-info">
                    <b>{i.name}</b>
                    {i.size && <span>Size: {i.size}</span>}
                    {i.color && <span>Color: {i.color}</span>}
                    <strong>₹{(i.price * i.qty).toLocaleString("en-IN")}</strong>
                  </div>
                  <div className="qty-row">
                    <button data-testid={`qty-minus-${i.id}`} onClick={() => setQty(i.id, i.size, i.qty - 1, i.color)} aria-label="Decrease quantity"><Minus size={14} /></button>
                    <span data-testid={`qty-${i.id}`}>{i.qty}</span>
                    <button data-testid={`qty-plus-${i.id}`} onClick={() => setQty(i.id, i.size, i.qty + 1, i.color)} aria-label="Increase quantity"><Plus size={14} /></button>
                  </div>
                  <button data-testid={`remove-${i.id}`} className="icon-button" onClick={() => remove(i.id, i.size, i.color)} aria-label="Remove item"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="cart-summary">
              <div><span>Total</span><strong data-testid="cart-total">₹{total.toLocaleString("en-IN")}</strong></div>
              <div className="cart-actions">
                <button data-testid="clear-cart-button" className="text-link as-button" onClick={clear}>Clear bag</button>
                <button data-testid="checkout-button" className="button primary" onClick={() => navigate("/checkout")}>Proceed to checkout <ArrowRight size={16} /></button>
              </div>
            </div>
          </>
        )}
        {items.length === 0 && (
          <Link data-testid="cart-browse-link" className="button primary" to="/collections">Browse collections <ArrowRight size={16} /></Link>
        )}
      </main>
      <Footer />
    </>
  );
}

export function CheckoutPage() {
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const { Razorpay } = useRazorpay();
  const [form, setForm] = useState({ name: "", mobile: "", address: "", city: "Nanded", pincode: "431604", delivery_type: "pickup", payment_method: "cod" });
  const [paymentsOn, setPaymentsOn] = useState(false);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config`).then(({ data }) => {
      setPaymentsOn(data.enabled);
      setProvider(data.provider);
      if (data.enabled && data.provider) setForm((f) => ({ ...f, payment_method: data.provider }));
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        ...form,
        items: items.map((i) => ({ product_id: i.id, qty: i.qty, size: i.size, color: i.color || "" })),
      });
      track("order_placed", { method: form.payment_method, total: data.total });
      if (form.payment_method === "gokwik" && provider === "gokwik") {
        const gkw = await axios.post(`${API}/payments/gokwik/create-order`, { order_id: data.order_id });
        const redirect = gkw.data.checkout?.checkoutUrl || gkw.data.checkout?.checkout_url;
        if (redirect) {
          clear();
          window.location.assign(redirect);
          return;
        }
        throw new Error("Payment checkout unavailable. Please try COD.");
      }
      if (form.payment_method === "razorpay" && provider === "razorpay") {
        const rzp = await axios.post(`${API}/payments/create-order`, { order_id: data.order_id });
        const rzpWindow = new Razorpay({
          key: rzp.data.key_id,
          amount: rzp.data.amount,
          currency: "INR",
          name: "Hyderabad Shopping Mall",
          description: `Order ${data.order_id.slice(-8)}`,
          order_id: rzp.data.id,
          prefill: { name: form.name, contact: form.mobile },
          theme: { color: "#800020" },
          handler: async (resp) => {
            await axios.post(`${API}/payments/verify`, { order_id: data.order_id, ...resp });
            clear();
            navigate(`/order-success?order=${data.order_id}`);
          },
        });
        rzpWindow.open();
        setLoading(false);
        return;
      }
      clear();
      navigate(`/order-success?order=${data.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place the order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!items.length)
    return (
      <>
        <Header />
        <main className="inner-page">
          <Chapter index="—" eyebrow="CHECKOUT" title="Your bag is empty" />
          <Link className="button primary" to="/collections">Browse collections <ArrowRight size={16} /></Link>
        </main>
        <Footer />
      </>
    );

  const deliveryCharge =
    form.delivery_type === "courier" ? (total >= CONFIG.courierFreeAbove ? 0 : CONFIG.deliveryCharges.courier) : CONFIG.deliveryCharges[form.delivery_type] ?? 0;
  const grandTotal = total + deliveryCharge;

  return (
    <>
      <Header />
      <main className="inner-page">
        <Chapter index="—" eyebrow="ALMOST DONE" title={<>Check<em>out</em></>} text={`${items.length} item(s) · Total ₹${total.toLocaleString("en-IN")}`} />
        <form data-testid="checkout-form" className="enquiry-form checkout-form" onSubmit={submit}>
          <div className="checkout-grid">
            <label>Name<input data-testid="checkout-name-input" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" autoComplete="name" /></label>
            <label>Mobile number<input data-testid="checkout-mobile-input" required type="tel" pattern="[0-9+\-\s]{10,20}" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit mobile" autoComplete="tel" /></label>
          </div>
          <label>Delivery address<textarea data-testid="checkout-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="House no, street, area (needed for delivery)" /></label>
          <div className="checkout-grid">
            <label>City<input data-testid="checkout-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
            <label>Pincode<input data-testid="checkout-pincode-input" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></label>
          </div>
          <div className="option-group">
            <span className="option-label">Delivery</span>
            <div className="option-row">
              {[["pickup", "Store pickup · Free"], ["local", "Nanded delivery · Free"], ["courier", `All-India courier · ₹${CONFIG.deliveryCharges.courier}`]].map(([v, l]) => (
                <button type="button" key={v} data-testid={`delivery-${v}`} className={form.delivery_type === v ? "option-chip active" : "option-chip"} onClick={() => setForm({ ...form, delivery_type: v })}>{l}</button>
              ))}
            </div>
            {form.delivery_type === "courier" && total < CONFIG.courierFreeAbove && (
              <span className="delivery-hint" data-testid="free-delivery-hint">Add ₹{(CONFIG.courierFreeAbove - total).toLocaleString("en-IN")} more for FREE courier delivery</span>
            )}
            {form.delivery_type === "courier" && total >= CONFIG.courierFreeAbove && (
              <span className="delivery-hint free" data-testid="free-delivery-applied">FREE courier delivery applied</span>
            )}
          </div>
          <div className="option-group">
            <span className="option-label">Payment</span>
            <div className="option-row">
              <button type="button" data-testid="pay-online" className={form.payment_method === provider ? "option-chip active" : "option-chip"} disabled={!paymentsOn} onClick={() => provider && setForm({ ...form, payment_method: provider })}>
                Online (UPI/Card){!paymentsOn && " — soon"}
              </button>
              <button type="button" data-testid="pay-cod" className={form.payment_method === "cod" ? "option-chip active" : "option-chip"} onClick={() => setForm({ ...form, payment_method: "cod" })}>Cash on delivery</button>
              <button type="button" data-testid="pay-pickup" className={form.payment_method === "pickup" ? "option-chip active" : "option-chip"} onClick={() => setForm({ ...form, payment_method: "pickup" })}>Pay at store</button>
            </div>
          </div>
          <div className="order-summary" data-testid="order-summary">
            <div><span>Items total</span><span>₹{total.toLocaleString("en-IN")}</span></div>
            <div><span>Delivery</span><span data-testid="delivery-charge">{deliveryCharge === 0 ? "FREE" : `₹${deliveryCharge.toLocaleString("en-IN")}`}</span></div>
            <div className="grand"><span>Grand total</span><strong data-testid="grand-total">₹{grandTotal.toLocaleString("en-IN")}</strong></div>
          </div>
          <button data-testid="place-order-button" className="button primary" disabled={loading}>
            {loading ? "Placing order…" : `Place order · ₹${grandTotal.toLocaleString("en-IN")}`} <ArrowRight size={16} />
          </button>
        </form>
      </main>
      <Footer />
    </>
  );
}

export function OrderSuccessPage() {
  const orderId = new URLSearchParams(window.location.search).get("order") || "";
  return (
    <>
      <Header />
      <main className="inner-page center-page">
        <Chapter index="✓" eyebrow="ORDER RECEIVED" title={<>Thank <em>you!</em></>} text="Your order has been received. Our team will call or WhatsApp you shortly to confirm availability, delivery and payment details." center />
        {orderId && <p className="catalogue-note" data-testid="order-id">Order reference: <b>{orderId.slice(-8).toUpperCase()}</b></p>}
        <div className="category-actions">
          <a data-testid="success-whatsapp-link" className="button primary" href={waLink(`my order ${orderId.slice(-8).toUpperCase()}`)} target="_blank" rel="noreferrer">Confirm on WhatsApp</a>
          <Link data-testid="success-track-link" className="text-link" to="/track">Track order <ArrowRight size={16} /></Link>
          <Link data-testid="success-continue-link" className="text-link" to="/collections">Continue shopping <ArrowRight size={16} /></Link>
        </div>
      </main>
      <Footer />
    </>
  );
}
